import { useApp } from '@/app/AppContext'
import type { DealForecast, ForecastFactor, ForecastStatus } from './dealApi'

/**
 * Прогноз модели рядом со статической вероятностью стадии (#128).
 *
 * ## Рядом, а не вместо
 *
 * Статическая вероятность — снимок стадии: её знают все, по ней считается взвешенная воронка,
 * и сверять прогноз есть с чем, только пока она на месте. Поэтому компонент всегда рисует
 * обе цифры: «50 % · прогноз 72 %».
 *
 * ## «Почему» — словами
 *
 * Три главных фактора приходят с сервера с вкладом каждого. Здесь они превращаются в фразы
 * («Активностей за 14 дней: 5 — повышает»): число вклада в логарифме шансов читателю ничего не
 * говорит, а направление и значение признака — говорят.
 *
 * ## Прогноза нет — сказать почему
 *
 * Пустое место рядом с вероятностью читается как «прогноз ноль» или «сломалось». Отсутствие
 * называется: модель ещё не обучалась, мало закрытых сделок, сделка ещё не посчитана.
 */

type Translate = (key: string, params?: Record<string, string | number>) => string

/** Фраза фактора: признак со значением и направление. */
export function factorText(t: Translate, factor: ForecastFactor): string {
  const value = factor.value == null ? t('forecast.value.none') : String(roundValue(factor.value))
  return t('forecast.factorLine', {
    factor: t(`forecast.factor.${factor.code}`, { value }),
    effect: t(factor.effect === 'up' ? 'forecast.effect.up' : 'forecast.effect.down'),
  })
}

function roundValue(value: number | string): number | string {
  return typeof value === 'number' ? Math.round(value * 10) / 10 : value
}

/** Почему прогноза нет — словами по состоянию модели. */
export function noForecastText(t: Translate, status: ForecastStatus | undefined): string {
  switch (status) {
    case 'trained':
      return t('forecast.none.pending')
    case 'insufficient_data':
      return t('forecast.none.insufficient')
    case 'none':
    case undefined:
      return t('forecast.none.untrained')
  }
}

export function formatPercent(value: number): string {
  return `${Math.round(value)} %`
}

/**
 * Чип «прогноз N %» с подсказкой из главных факторов. Без прогноза — причина словами.
 *
 * Для доски: одна строка, факторы в подсказке и в `aria-label`. На карточке сделки факторы
 * показываются списком (`ForecastFactors`).
 */
export function ForecastChip({
  forecast,
  status,
}: {
  forecast: DealForecast | null | undefined
  status: ForecastStatus | undefined
}) {
  const { t } = useApp()
  if (forecast == null) {
    return (
      <span className="t-xs muted" data-testid="forecast-none">
        {noForecastText(t, status)}
      </span>
    )
  }
  const why = forecast.factors.map((factor) => factorText(t, factor)).join('; ')
  const label = t('forecast.short', { probability: formatPercent(forecast.probability) })
  return (
    <span
      className="forecast-chip"
      data-testid="forecast"
      title={why ? t('forecast.why', { factors: why }) : undefined}
      aria-label={why ? `${label}. ${t('forecast.why', { factors: why })}` : label}
    >
      {label}
    </span>
  )
}

/** Главные факторы списком — для карточки сделки, где под них есть место. */
export function ForecastFactors({ forecast }: { forecast: DealForecast }) {
  const { t } = useApp()
  if (forecast.factors.length === 0) {
    return <span className="t-xs muted">{t('forecast.noFactors')}</span>
  }
  return (
    <ul className="forecast-factors" aria-label={t('forecast.whyTitle')}>
      {forecast.factors.map((factor) => (
        <li key={factor.code} data-effect={factor.effect}>
          {factorText(t, factor)}
        </li>
      ))}
    </ul>
  )
}
